/**
 * WebSocket Hook
 * Manages WebSocket connections and real-time events
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuthStore } from '@store/authStore';
import logger from '@utils/logger';

interface UseWebSocketOptions {
  autoConnect?: boolean;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
}

interface WebSocketState {
  connected: boolean;
  connecting: boolean;
  error: string | null;
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const {
    autoConnect = true,
    reconnection = true,
    reconnectionAttempts = 5,
    reconnectionDelay = 1000,
  } = options;

  const [state, setState] = useState<WebSocketState>({
    connected: false,
    connecting: false,
    error: null,
  });

  const socketRef = useRef<Socket | null>(null);
  const { accessToken: token } = useAuthStore();

  // Connect to WebSocket server
  const connect = useCallback(() => {
    if (socketRef.current?.connected) {
      logger.warn('Already connected to WebSocket');
      return;
    }

    if (!token) {
      logger.error('No authentication token available');
      setState((prev) => ({ ...prev, error: 'Authentication required' }));
      return;
    }

    setState((prev) => ({ ...prev, connecting: true, error: null }));

    // In production, websocket is proxied through Nginx, in dev it's localhost:3004
    const wsUrl = window.location.protocol === 'https:' 
      ? `https://${window.location.host}` 
      : 'http://localhost:3004';
    logger.info(`🔌 Connecting to WebSocket: ${wsUrl}`);

    socketRef.current = io(wsUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection,
      reconnectionAttempts,
      reconnectionDelay,
    });

    // Connection events
    socketRef.current.on('connect', () => {
      logger.info('✅ WebSocket connected');
      setState({ connected: true, connecting: false, error: null });
    });

    socketRef.current.on('disconnect', (reason) => {
      logger.warn('WebSocket disconnected:', reason);
      setState({ connected: false, connecting: false, error: reason });
    });

    socketRef.current.on('connect_error', (error) => {
      logger.error('WebSocket connection error:', error);
      setState((prev) => ({ ...prev, connecting: false, error: error.message }));
    });

    socketRef.current.on('error', (error) => {
      logger.error('WebSocket error:', error);
      setState((prev) => ({ ...prev, error: error.message }));
    });

    socketRef.current.on('reconnect_attempt', (attemptNumber) => {
      logger.info(`WebSocket reconnection attempt ${attemptNumber}`);
      setState((prev) => ({ ...prev, connecting: true }));
    });

    socketRef.current.on('reconnect_failed', () => {
      logger.error('WebSocket reconnection failed');
      setState({ connected: false, connecting: false, error: 'Reconnection failed' });
    });
  }, [token, reconnection, reconnectionAttempts, reconnectionDelay]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setState({ connected: false, connecting: false, error: null });
      logger.info('WebSocket disconnected manually');
    }
  }, []);

  // Emit event
  const emit = useCallback((event: string, data?: any) => {
    if (!socketRef.current?.connected) {
      logger.warn('Cannot emit - WebSocket not connected');
      return;
    }

    socketRef.current.emit(event, data);
    logger.debug(`Emitted event: ${event}`, data);
  }, []);

  // Listen to event
  const on = useCallback((event: string, handler: (...args: any[]) => void) => {
    if (!socketRef.current) {
      logger.warn('Cannot listen - WebSocket not initialized');
      return;
    }

    socketRef.current.on(event, handler);
    logger.debug(`Listening to event: ${event}`);

    // Return cleanup function
    return () => {
      socketRef.current?.off(event, handler);
    };
  }, []);

  // Remove event listener
  const off = useCallback((event: string, handler?: (...args: any[]) => void) => {
    if (!socketRef.current) return;

    if (handler) {
      socketRef.current.off(event, handler);
    } else {
      socketRef.current.off(event);
    }

    logger.debug(`Removed listener for event: ${event}`);
  }, []);

  // Join room
  const joinRoom = useCallback((room: string) => {
    emit('join:room', room);
    logger.info(`Joining room: ${room}`);
  }, [emit]);

  // Leave room
  const leaveRoom = useCallback((room: string) => {
    emit('leave:room', room);
    logger.info(`Leaving room: ${room}`);
  }, [emit]);

  // Subscribe to analytics updates
  const subscribeToAnalytics = useCallback((filters?: any) => {
    emit('subscribe:analytics', filters);
    logger.info('Subscribed to analytics updates');
  }, [emit]);

  // Subscribe to anomaly alerts
  const subscribeToAnomalies = useCallback(() => {
    emit('subscribe:anomalies');
    logger.info('Subscribed to anomaly alerts');
  }, [emit]);

  // Subscribe to session updates
  const subscribeToSessions = useCallback((sessionId?: string) => {
    emit('subscribe:sessions', sessionId);
    logger.info('Subscribed to session updates');
  }, [emit]);

  // Auto-connect on mount
  useEffect(() => {
    logger.info('🔌 WebSocket Hook - autoConnect:', autoConnect, 'hasToken:', !!token);
    
    if (autoConnect && token) {
      connect();
    } else if (autoConnect && !token) {
      logger.warn('⚠️  WebSocket autoConnect skipped - no authentication token');
    }

    return () => {
      disconnect();
    };
  }, [autoConnect, token, connect, disconnect]);

  return {
    ...state,
    socket: socketRef.current,
    connect,
    disconnect,
    emit,
    on,
    off,
    joinRoom,
    leaveRoom,
    subscribeToAnalytics,
    subscribeToAnomalies,
    subscribeToSessions,
  };
};

